"use client";

import { useContext, forwardRef } from "react";
import styled from "styled-components";
import { borderRadius, backgroundColorLevel1 } from "@churchofjesuschrist/eden-style-constants";
import { sharedCheckCss } from "../utils/sharedCss";
import { AppearanceContext } from "@churchofjesuschrist/eden-appearance";

/**
 * What is with the :before and mask stuff below?
 * In short, Safari will not allow us to change base64 encoded svg colors via prefers-color-scheme. So we had to hack around it. The mask lets us use the SVG as the mask shape and the background color as what is shown through. See EDEN-2298 for more details.
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const InternalUseOnlyCheckbox = styled.input.attrs({
  type: "checkbox"
}).withConfig({
  componentId: "sc-csv0c-0"
})(["", ";border-radius:", ";position:relative;&::before{content:\"\";position:absolute;}&:checked{&::before{height:0.36rem;width:2px;border-radius:1px;bottom:0.125rem;left:0.125rem;transform:rotate(-40deg);transform-origin:60% 30%;background-color:", ";}&::after{content:\"\";position:absolute;height:2px;width:0.82rem;border-radius:1px;bottom:0.125rem;left:0.125rem;transform:rotate(-44deg);transform-origin:12% -123%;background-color:", ";}}&:indeterminate{&::before{width:0.75rem;height:2px;top:50%;transform:translateY(-50%);left:0.125rem;border-radius:1px;background-color:", ";}}"], sharedCheckCss, borderRadius, backgroundColorLevel1, backgroundColorLevel1, backgroundColorLevel1);

/**
 * `Checkbox` can be used when several answers or no answer at all can correctly apply to a question. They can also be used when there is a list of settings that can be enabled or disabled.
 * @param {object} props - The component props
 * @param {boolean} [props.checked] - The checked state for controlled use.
 * @param {boolean} [props.defaultChecked] - Default checked state for uncontrolled use.
 * @param {boolean} [props.disabled] - Makes the input not mutable, focusable, or even submitted with the form.
 * @param {string} [props.name] - Used to reference elements in JavaScript, or to reference form data after a form is submitted.
 * @param {Function} [props.onChange] - Callback to execute when the checkbox is toggled.
 * @param {boolean} [props.required] - Whether the input is required to be filled out to validate.
 * @param {boolean} [props.indeterminate] - Whether the input is in an indeterminate.
 * @param {string} [props.value] - Value of the checkbox.
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} - The Checkbox component
 */
const CheckboxComponent = (props, ref) => {
  const curAppearanceContext = useContext(AppearanceContext);
  return /*#__PURE__*/_jsx(InternalUseOnlyCheckbox, {
    ref: ref,
    ...props,
    $curAppearanceContext: curAppearanceContext
  });
};
const Checkbox = /*#__PURE__*/forwardRef(CheckboxComponent);
Checkbox.displayName = "Checkbox";

// Custom property for the FormField to consume
Checkbox.isInput = true;
export default Checkbox;